import React, { useEffect, useState } from 'react';
import {
  getUbyportConfig,
  updateUbyportConfig,
  testUbyportConnection,
  getCodebookEntries,
  updateCodebookEntry,
  bulkUpdateCodebookEntries,
  toggleCodebookEntryEnabled,
  getSyncStatus,
  forceSyncCodebookType,
  forceSyncAllCodebooks,
  UbyportConfigDTO,
  UbyportCodeEntryAdminDTO,
  UbyportSyncStatusDTO,
  UbyportSyncKeyField,
} from '../api.ubyport';

const getToken = () => localStorage.getItem('token') || '';

type TabType = 'config' | 'codebook' | 'sync';
type CodebookType = 'STATE' | 'PURPOSE' | 'ERROR';
type ColumnKey =
  | 'id'
  | 'type'
  | 'ubyportId'
  | 'kod2'
  | 'kod3'
  | 'platiOd'
  | 'platiDo'
  | 'textCZ'
  | 'textENG'
  | 'textKratkyCZ'
  | 'textKratkyENG'
  | 'translationCZ'
  | 'translationEN'
  | 'enabled'
  | 'needVisa';

const CODEBOOK_COLUMNS: { key: ColumnKey; label: string }[] = [
  { key: 'id', label: 'ID' },
  { key: 'type', label: 'Type' },
  { key: 'ubyportId', label: 'Ubyport ID' },
  { key: 'kod2', label: 'Kod2' },
  { key: 'kod3', label: 'Kod3' },
  { key: 'platiOd', label: 'Plati Od' },
  { key: 'platiDo', label: 'Plati Do' },
  { key: 'textCZ', label: 'Text CZ' },
  { key: 'textENG', label: 'Text ENG' },
  { key: 'textKratkyCZ', label: 'Short CZ' },
  { key: 'textKratkyENG', label: 'Short ENG' },
  { key: 'translationCZ', label: 'Translation CZ' },
  { key: 'translationEN', label: 'Translation EN' },
  { key: 'enabled', label: 'Enabled' },
  { key: 'needVisa', label: 'Need Visa' },
];

const DEFAULT_VISIBLE_COLUMNS: Record<ColumnKey, boolean> = CODEBOOK_COLUMNS.reduce((acc, col) => {
  acc[col.key] = true;
  return acc;
}, {} as Record<ColumnKey, boolean>);

const getColumnsStorageKey = (type: CodebookType) => `ubyport-columns:${type}`;

const loadColumnsForType = (type: CodebookType): Record<ColumnKey, boolean> => {
  try {
    const raw = localStorage.getItem(getColumnsStorageKey(type));
    if (!raw) return { ...DEFAULT_VISIBLE_COLUMNS };
    const parsed = JSON.parse(raw) as Partial<Record<ColumnKey, boolean>>;
    return {
      ...DEFAULT_VISIBLE_COLUMNS,
      ...parsed,
    };
  } catch {
    return { ...DEFAULT_VISIBLE_COLUMNS };
  }
};

interface EditingEntry {
  [key: number]: Partial<UbyportCodeEntryAdminDTO>;
}

const GUEST_FORM_FIELD_OPTIONS = [
  'ubyportId',
  'kod2',
  'kod3',
  'textCZ',
  'textENG',
  'textKratkyCZ',
  'textKratkyENG',
  'translationCZ',
  'translationEN',
] as const;

const asSafeString = (value: unknown, fallback = ''): string => {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  return trimmed.length > 0 ? value : fallback;
};

const SYNC_KEY_OPTIONS: UbyportSyncKeyField[] = ['UBYPORT_ID', 'KOD2', 'KOD3', 'KOD2_KOD3', 'KOD3_KOD2'];

const UbyportAdmin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('config');
  const [isMobile, setIsMobile] = useState<boolean>(() => window.innerWidth <= 768);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Config tab state
  const [config, setConfig] = useState<UbyportConfigDTO | null>(null);
  const [testingUrl, setTestingUrl] = useState('');
  const [productionUrl, setProductionUrl] = useState('');
  const [testingMode, setTestingMode] = useState(true);
  const [testingConnection, setTestingConnection] = useState(false);
  // Codebook tab state
  const [selectedCodebookType, setSelectedCodebookType] = useState<CodebookType>('STATE');
  const [entries, setEntries] = useState<UbyportCodeEntryAdminDTO[]>([]);
  const [showDisabled, setShowDisabled] = useState(false);
  const [editingEntries, setEditingEntries] = useState<EditingEntry>({});
  const [isBulkEditMode, setIsBulkEditMode] = useState(false);
  const [isSavingBulk, setIsSavingBulk] = useState(false);
  const [lastCodebookSaveMs, setLastCodebookSaveMs] = useState<number | null>(null);
  const [lastCodebookSaveKind, setLastCodebookSaveKind] = useState<'single' | 'bulk' | null>(null);
  const [visibleColumnsByType, setVisibleColumnsByType] = useState<Record<CodebookType, Record<ColumnKey, boolean>>>(() => ({
    STATE: loadColumnsForType('STATE'),
    PURPOSE: loadColumnsForType('PURPOSE'),
    ERROR: loadColumnsForType('ERROR'),
  }));
  // Sync tab state
  const [syncStatus, setSyncStatus] = useState<UbyportSyncStatusDTO[]>([]);
  // Shared admin/dev account fields
  const [sharedUsername, setSharedUsername] = useState('');
  const [sharedPassword, setSharedPassword] = useState('');
  const [sharedDomain, setSharedDomain] = useState('');
  const [stateDisplayColumnCZ, setStateDisplayColumnCZ] = useState('textCZ');
  const [stateDisplayColumnEN, setStateDisplayColumnEN] = useState('textENG');
  const [stateSubmitColumn, setStateSubmitColumn] = useState('kod3');
  const [purposeDisplayColumnCZ, setPurposeDisplayColumnCZ] = useState('textCZ');
  const [purposeDisplayColumnEN, setPurposeDisplayColumnEN] = useState('textENG');
  const [purposeSubmitColumn, setPurposeSubmitColumn] = useState('kod2');
  const [stateSyncKeyField, setStateSyncKeyField] = useState<UbyportSyncKeyField>('KOD3');
  const [purposeSyncKeyField, setPurposeSyncKeyField] = useState<UbyportSyncKeyField>('KOD2');
  const [errorSyncKeyField, setErrorSyncKeyField] = useState<UbyportSyncKeyField>('KOD2');
  const [complianceNoticeCZ, setComplianceNoticeCZ] = useState('');
  const [complianceNoticeEN, setComplianceNoticeEN] = useState('');

    useEffect(() => {
    if (activeTab === 'config') {
      fetchConfig();
    } else if (activeTab === 'codebook') {
      fetchCodebookEntries(selectedCodebookType);
    } else if (activeTab === 'sync') {
      fetchSyncStatus();
    }
     }, [activeTab, selectedCodebookType, showDisabled]);

    useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    }, []);

    const pagePadding = isMobile ? '12px' : '20px';
    const twoColGrid = isMobile ? '1fr' : '1fr 1fr';
    // ─────────────────────────────────────────────────────────────────────────
    // Config Tab Functions
    // ─────────────────────────────────────────────────────────────────────────
  const extractErrorMessage = (err: any, fallback: string) => {
    return err?.response?.data?.lastTestResult || err?.response?.data?.message || err?.message || fallback;
  };

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUbyportConfig(getToken());
      setConfig(data);
      setTestingUrl(asSafeString(data.testingUrl));
      setProductionUrl(asSafeString(data.productionUrl));
      setTestingMode(Boolean(data.testingModeEnabled));
      setSharedUsername(asSafeString(data.sharedUsername));
      setSharedPassword('');
      setSharedDomain(asSafeString(data.sharedDomain));
      setStateDisplayColumnCZ(asSafeString(data.stateDisplayColumnCZ, 'textCZ'));
      setStateDisplayColumnEN(asSafeString(data.stateDisplayColumnEN, 'textENG'));
      setStateSubmitColumn(asSafeString(data.stateSubmitColumn, 'kod3'));
      setPurposeDisplayColumnCZ(asSafeString(data.purposeDisplayColumnCZ, 'textCZ'));
      setPurposeDisplayColumnEN(asSafeString(data.purposeDisplayColumnEN, 'textENG'));
      setPurposeSubmitColumn(asSafeString(data.purposeSubmitColumn, 'kod2'));
      setStateSyncKeyField(data.stateSyncKeyField || 'KOD3');
      setPurposeSyncKeyField(data.purposeSyncKeyField || 'KOD2');
      setErrorSyncKeyField(data.errorSyncKeyField || 'KOD2');
      setComplianceNoticeCZ(typeof data.complianceNoticeCZ === 'string' ? data.complianceNoticeCZ : '');
      setComplianceNoticeEN(typeof data.complianceNoticeEN === 'string' ? data.complianceNoticeEN : '');
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load configuration'));
    } finally {
      setLoading(false);
    }
  };
  const handleSaveConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const updated = await updateUbyportConfig(
        {
          testingUrl: testingUrl || undefined,
          productionUrl: productionUrl || undefined,
          testingModeEnabled: testingMode,
          sharedUsername: sharedUsername || undefined,
          sharedPassword: sharedPassword || undefined,
          sharedDomain: sharedDomain || undefined,
          stateDisplayColumnCZ,
          stateDisplayColumnEN,
          stateSubmitColumn,
          purposeDisplayColumnCZ,
          purposeDisplayColumnEN,
          purposeSubmitColumn,
          stateSyncKeyField,
          purposeSyncKeyField,
          errorSyncKeyField,
          complianceNoticeCZ,
          complianceNoticeEN,
        },
        getToken()
      );
      setConfig(updated);
      setTestingUrl(asSafeString(updated.testingUrl));
      setProductionUrl(asSafeString(updated.productionUrl));
      setTestingMode(Boolean(updated.testingModeEnabled));
      setSharedUsername(asSafeString(updated.sharedUsername));
      setSharedDomain(asSafeString(updated.sharedDomain));
      setSharedPassword('');
      setStateDisplayColumnCZ(asSafeString(updated.stateDisplayColumnCZ, 'textCZ'));
      setStateDisplayColumnEN(asSafeString(updated.stateDisplayColumnEN, 'textENG'));
      setStateSubmitColumn(asSafeString(updated.stateSubmitColumn, 'kod3'));
      setPurposeDisplayColumnCZ(asSafeString(updated.purposeDisplayColumnCZ, 'textCZ'));
      setPurposeDisplayColumnEN(asSafeString(updated.purposeDisplayColumnEN, 'textENG'));
      setPurposeSubmitColumn(asSafeString(updated.purposeSubmitColumn, 'kod2'));
      setStateSyncKeyField(updated.stateSyncKeyField || 'KOD3');
      setPurposeSyncKeyField(updated.purposeSyncKeyField || 'KOD2');
      setErrorSyncKeyField(updated.errorSyncKeyField || 'KOD2');
      setComplianceNoticeCZ(typeof updated.complianceNoticeCZ === 'string' ? updated.complianceNoticeCZ : '');
      setComplianceNoticeEN(typeof updated.complianceNoticeEN === 'string' ? updated.complianceNoticeEN : '');
      setSuccess('Configuration saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to save configuration'));
    } finally {
      setLoading(false);
    }
  };
  const handleTestConnection = async () => {
    setTestingConnection(true);
    setError(null);
    try {
      const result = await testUbyportConnection(
        {
          testingUrl: testingUrl || undefined,
          productionUrl: productionUrl || undefined,
          testingModeEnabled: testingMode,
          sharedUsername: sharedUsername || undefined,
          sharedPassword: sharedPassword || undefined,
          sharedDomain: sharedDomain || undefined,
        },
        getToken()
      );
      setConfig(result);
      setSuccess(`Connection test: ${result.lastTestResult || 'No result'}`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Connection test failed'));
    } finally {
      setTestingConnection(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────
  // Codebook Tab Functions
  // ─────────────────────────────────────────────────────────────────────────
  const fetchCodebookEntries = async (type: CodebookType) => {
    setLoading(true);
    setError(null);
    setEditingEntries({});
    try {
      const data = await getCodebookEntries(type, getToken(), showDisabled);
      setEntries(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load codebook entries');
    } finally {
      setLoading(false);
    }
  };

  const toUpdatePayload = (edited?: Partial<UbyportCodeEntryAdminDTO>) => ({
    translationCZ: edited?.translationCZ,
    translationEN: edited?.translationEN,
    enabled: edited?.enabled,
    needVisa: typeof edited?.needVisa === 'number' ? edited.needVisa : undefined,
  });

  const handleStartEdit = (id: number) => {
    const entry = entries.find(e => e.id === id);
    if (entry) {
      setEditingEntries(prev => ({
        ...prev,
        [id]: { ...entry },
      }));
    }
  };
  const handleCancelEdit = (id: number) => {
    setEditingEntries(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };
  const handleDiscardAllEdits = () => {
    setEditingEntries({});
    setError(null);
  };
  const handleUpdateField = (id: number, field: string, value: any) => {
    setEditingEntries(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };
  const handleSaveEntry = async (id: number) => {
    setError(null);
    const startedAt = performance.now();
    try {
      const edited = editingEntries[id];
      const updated = await updateCodebookEntry(
        id,
        toUpdatePayload(edited),
        getToken()
      );
      setEntries(prev => prev.map(e => (e.id === id ? updated : e)));
      setEditingEntries(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      const duration = Math.max(1, Math.round(performance.now() - startedAt));
      setLastCodebookSaveMs(duration);
      setLastCodebookSaveKind('single');
      setSuccess('Entry updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update entry');
    }
  };

  const handleToggleEnabled = async (id: number, currentlyEnabled: boolean) => {
    setError(null);
    try {
      const updated = await toggleCodebookEntryEnabled(id, !currentlyEnabled, getToken());
      setEntries(prev => prev.map(e => (e.id === id ? updated : e)));
      setSuccess(`Entry ${updated.enabled ? 'enabled' : 'disabled'} successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to toggle entry');
    }
  };

  const handleSaveAllEdits = async () => {
    const ids = Object.keys(editingEntries).map(Number);
    if (ids.length === 0) {
      setSuccess('No pending changes to save');
      setTimeout(() => setSuccess(null), 2000);
      return;
    }

    setIsSavingBulk(true);
    setError(null);
    const startedAt = performance.now();

    try {
      const payload = ids.map((id) => ({ id, ...toUpdatePayload(editingEntries[id]) }));
      const updatedRows = await bulkUpdateCodebookEntries(payload, getToken());
      const updatedById = new Map(updatedRows.map((row) => [row.id, row]));

      setEntries(prev => prev.map(e => updatedById.get(e.id) || e));
      setEditingEntries({});
      const duration = Math.max(1, Math.round(performance.now() - startedAt));
      setLastCodebookSaveMs(duration);
      setLastCodebookSaveKind('bulk');
      setSuccess(`Saved ${updatedRows.length} row(s) successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save bulk changes');
    } finally {
      setIsSavingBulk(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────
  // Sync Tab Functions
  // ─────────────────────────────────────────────────────────────────────────
  const fetchSyncStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSyncStatus(getToken());
      setSyncStatus(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load sync status');
    } finally {
      setLoading(false);
    }
  };
  const handleForceSyncType = async (type: string) => {
    setError(null);
    try {
      const updated = await forceSyncCodebookType(type, getToken());
      const newStatus = syncStatus.map(s => (s.type === type ? updated : s));
      setSyncStatus(newStatus);
      setSuccess(`Sync completed for ${type}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || `Failed to sync ${type}`);
    }
  };
  const handleForceSyncAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const updated = await forceSyncAllCodebooks(getToken());
      setSyncStatus(updated);
      setSuccess('All codebooks synced successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to sync all codebooks');
    } finally {
      setLoading(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────
  // UI Helpers
  // ─────────────────────────────────────────────────────────────────────────
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  const renderConfigTab = () => (
    <div style={{ padding: pagePadding }}>
      <h3>Shared Admin / Dev Ubyport Configuration</h3>
      <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f7fafc', borderRadius: '8px', color: '#2d3748' }}>
        <div style={{ fontWeight: 600, marginBottom: '6px' }}>Scope of this section</div>
        <div style={{ fontSize: '0.92rem', color: '#4a5568' }}>
          This shared configuration is for the admin/developer Ubyport account used for codebook sync and diagnostics.
          Apartment communication should use each apartment&apos;s own Ubyport username, password/passport, and IDUB from the apartment detail screen.
        </div>
      </div>
      {loading && <p>Loading...</p>}
      {config && (
        <div style={{ maxWidth: isMobile ? '100%' : '600px' }}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              <strong>Current Shared Endpoint Mode:</strong>
            </label>
            <p style={{ fontSize: '18px', color: testingMode ? '#ff9800' : '#4caf50' }}>
              {testingMode ? 'TESTING' : 'PRODUCTION'}
            </p>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              <strong>Shared Testing URL:</strong>
            </label>
            <input
              type="text"
              value={testingUrl}
              onChange={e => setTestingUrl(e.target.value)}
              style={{ width: '100%', padding: '8px', marginBottom: '10px' }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              <strong>Shared Production URL:</strong>
            </label>
            <input
              type="text"
              value={productionUrl}
              onChange={e => setProductionUrl(e.target.value)}
              style={{ width: '100%', padding: '8px', marginBottom: '10px' }}
            />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '10px' }}>
              <input
                type="radio"
                name="mode"
                checked={testingMode}
                onChange={() => setTestingMode(true)}
              />
              <span style={{ marginLeft: '8px' }}>Testing Mode</span>
            </label>
            <label style={{ display: 'block', marginBottom: '20px' }}>
              <input
                type="radio"
                name="mode"
                checked={!testingMode}
                onChange={() => setTestingMode(false)}
              />
              <span style={{ marginLeft: '8px' }}>Production Mode</span>
            </label>
          </div>
          {config.lastTestAt && (
            <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f5f5f5' }}>
              <p><strong>Last Test:</strong> {formatDate(config.lastTestAt)}</p>
              <p><strong>Result:</strong> {config.lastTestResult || 'No result'}</p>
            </div>
          )}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={handleSaveConfig}
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                width: isMobile ? '100%' : 'auto',
              }}
            >
              Save Configuration
            </button>
            <button
              onClick={handleTestConnection}
              disabled={testingConnection || !config}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2196f3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: testingConnection ? 'not-allowed' : 'pointer',
                width: isMobile ? '100%' : 'auto',
              }}
            >
              {testingConnection ? 'Testing...' : 'Test Connection'}
            </button>
          </div>

          <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '2px solid #ddd' }}>
            <h4 style={{ marginBottom: '15px' }}>Shared Admin / Dev Account</h4>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>
                <strong>Shared Ubyport Username:</strong>
              </label>
              <input
                type="text"
                value={sharedUsername}
                onChange={e => setSharedUsername(e.target.value)}
                style={{ width: '100%', padding: '8px', marginBottom: '10px' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>
                <strong>Shared Ubyport Password / Passport:</strong>
              </label>
              <input
                type="password"
                value={sharedPassword}
                onChange={e => setSharedPassword(e.target.value)}
                placeholder={config.hasSharedPassword ? 'Stored shared secret is kept unless you enter a new one' : 'Enter shared admin/dev secret'}
                style={{ width: '100%', padding: '8px', marginBottom: '6px' }}
              />
              <div style={{ fontSize: '0.85rem', color: '#718096' }}>
                {config.hasSharedPassword
                  ? 'A shared secret is already stored. Leave this blank to keep it unchanged.'
                  : 'No shared secret stored yet.'}
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>
                <strong>Shared Domain:</strong>
              </label>
              <input
                type="text"
                value={sharedDomain}
                onChange={e => setSharedDomain(e.target.value)}
                style={{ width: '100%', padding: '8px', marginBottom: '10px' }}
              />
            </div>
          </div>

          <div style={{ marginTop: '24px', padding: '12px', border: '1px solid #ddd', borderRadius: '8px' }}>
            <h4 style={{ marginTop: 0 }}>Guest Form Field Mapping</h4>
            <p style={{ marginTop: 0, color: '#4a5568' }}>
              Choose submit values plus separate Czech and English labels for the guest form.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: twoColGrid, gap: '12px', marginBottom: '12px' }}>
              <label>
                <strong>State display field (CZ)</strong>
                <select value={stateDisplayColumnCZ} onChange={e => setStateDisplayColumnCZ(e.target.value)} style={{ width: '100%', padding: '8px', marginTop: '6px' }}>
                  {GUEST_FORM_FIELD_OPTIONS.map(f => <option key={`state-display-cz-${f}`} value={f}>{f}</option>)}
                </select>
              </label>
              <label>
                <strong>State display field (EN)</strong>
                <select value={stateDisplayColumnEN} onChange={e => setStateDisplayColumnEN(e.target.value)} style={{ width: '100%', padding: '8px', marginTop: '6px' }}>
                  {GUEST_FORM_FIELD_OPTIONS.map(f => <option key={`state-display-en-${f}`} value={f}>{f}</option>)}
                </select>
              </label>
              <label>
                <strong>State submit field</strong>
                <select value={stateSubmitColumn} onChange={e => setStateSubmitColumn(e.target.value)} style={{ width: '100%', padding: '8px', marginTop: '6px' }}>
                  {GUEST_FORM_FIELD_OPTIONS.map(f => <option key={`state-submit-${f}`} value={f}>{f}</option>)}
                </select>
              </label>
              <div />
              <label>
                <strong>Purpose display field (CZ)</strong>
                <select value={purposeDisplayColumnCZ} onChange={e => setPurposeDisplayColumnCZ(e.target.value)} style={{ width: '100%', padding: '8px', marginTop: '6px' }}>
                  {GUEST_FORM_FIELD_OPTIONS.map(f => <option key={`purpose-display-cz-${f}`} value={f}>{f}</option>)}
                </select>
              </label>
              <label>
                <strong>Purpose display field (EN)</strong>
                <select value={purposeDisplayColumnEN} onChange={e => setPurposeDisplayColumnEN(e.target.value)} style={{ width: '100%', padding: '8px', marginTop: '6px' }}>
                  {GUEST_FORM_FIELD_OPTIONS.map(f => <option key={`purpose-display-en-${f}`} value={f}>{f}</option>)}
                </select>
              </label>
              <label>
                <strong>Purpose submit field</strong>
                <select value={purposeSubmitColumn} onChange={e => setPurposeSubmitColumn(e.target.value)} style={{ width: '100%', padding: '8px', marginTop: '6px' }}>
                  {GUEST_FORM_FIELD_OPTIONS.map(f => <option key={`purpose-submit-${f}`} value={f}>{f}</option>)}
                </select>
              </label>
            </div>
          </div>

          <div style={{ marginTop: '18px', padding: '12px', border: '1px solid #ddd', borderRadius: '8px' }}>
            <h4 style={{ marginTop: 0 }}>Public Check-in Compliance Notice</h4>
            <p style={{ marginTop: 0, color: '#4a5568' }}>
              This text is shown on the public start page before guests continue to the form.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: twoColGrid, gap: '12px' }}>
              <label>
                <strong>Compliance notice (CZ)</strong>
                <textarea
                  value={complianceNoticeCZ}
                  onChange={e => setComplianceNoticeCZ(e.target.value)}
                  style={{ width: '100%', minHeight: '100px', padding: '8px', marginTop: '6px' }}
                />
              </label>
              <label>
                <strong>Compliance notice (EN)</strong>
                <textarea
                  value={complianceNoticeEN}
                  onChange={e => setComplianceNoticeEN(e.target.value)}
                  style={{ width: '100%', minHeight: '100px', padding: '8px', marginTop: '6px' }}
                />
              </label>
            </div>
          </div>

          <div style={{ marginTop: '24px', padding: '12px', border: '1px solid #ddd', borderRadius: '8px' }}>
            <h4 style={{ marginTop: 0 }}>Codebook Sync Identity</h4>
            <p style={{ marginTop: 0, color: '#4a5568' }}>
              Choose which source field is treated as the primary sync key for each codebook. This controls how Ubyport rows are matched to cached DB rows during sync.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: twoColGrid, gap: '12px' }}>
              <label>
                <strong>State sync key</strong>
                <select value={stateSyncKeyField} onChange={e => setStateSyncKeyField(e.target.value as UbyportSyncKeyField)} style={{ width: '100%', padding: '8px', marginTop: '6px' }}>
                  {SYNC_KEY_OPTIONS.map(f => <option key={`state-sync-${f}`} value={f}>{f}</option>)}
                </select>
              </label>
              <label>
                <strong>Purpose sync key</strong>
                <select value={purposeSyncKeyField} onChange={e => setPurposeSyncKeyField(e.target.value as UbyportSyncKeyField)} style={{ width: '100%', padding: '8px', marginTop: '6px' }}>
                  {SYNC_KEY_OPTIONS.map(f => <option key={`purpose-sync-${f}`} value={f}>{f}</option>)}
                </select>
              </label>
              <label>
                <strong>Error sync key</strong>
                <select value={errorSyncKeyField} onChange={e => setErrorSyncKeyField(e.target.value as UbyportSyncKeyField)} style={{ width: '100%', padding: '8px', marginTop: '6px' }}>
                  {SYNC_KEY_OPTIONS.map(f => <option key={`error-sync-${f}`} value={f}>{f}</option>)}
                </select>
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  const renderCell = (
    entry: UbyportCodeEntryAdminDTO,
    isEditing: Partial<UbyportCodeEntryAdminDTO> | undefined,
    key: ColumnKey,
    rowIsEditing: boolean
  ) => {
    switch (key) {
      case 'translationCZ':
      case 'translationEN':
        if (rowIsEditing) {
          const currentValue = (isEditing?.[key] as string | undefined) ?? (entry[key] as string | undefined) ?? '';
          return (
            <input
              type="text"
              value={currentValue}
              onChange={e => handleUpdateField(entry.id, key, e.target.value)}
              style={{ width: '100%' }}
            />
          );
        }
        return (entry[key] as string) || '-';

      case 'textCZ':
      case 'textENG':
      case 'textKratkyCZ':
      case 'textKratkyENG':
        return (entry[key] as string) || '-';

      case 'enabled': {
        const checkedValue = rowIsEditing
          ? (isEditing?.enabled ?? entry.enabled) !== false
          : entry.enabled;

        return (
          <input
            type="checkbox"
            checked={checkedValue}
            onChange={e => {
              if (rowIsEditing) {
                handleUpdateField(entry.id, 'enabled', e.target.checked);
              } else {
                handleToggleEnabled(entry.id, entry.enabled);
              }
            }}
          />
        );
      }

      case 'needVisa': {
        const currentValue = rowIsEditing
          ? (typeof isEditing?.needVisa === 'number' ? isEditing.needVisa : entry.needVisa) ?? -1
          : entry.needVisa ?? -1;
        if (entry.type !== 'STATE') return '-';
        return (
          <input
            type="checkbox"
            checked={currentValue === 1}
            disabled={!rowIsEditing}
            onChange={e => handleUpdateField(entry.id, 'needVisa', e.target.checked ? 1 : 0)}
          />
        );
      }

      default:
        return String((rowIsEditing ? isEditing?.[key] : undefined) ?? entry[key] ?? '-');
    }
  };
  const renderCodebookTab = () => (
    <div style={{ padding: pagePadding }}>
      <h3>Shared Ubyport Codebook Management</h3>
      <p style={{ color: '#4a5568', marginTop: 0 }}>
        These codebooks are synced via the shared admin/dev Ubyport account and then used by the application.
      </p>
      <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: isMobile ? '8px' : '20px' }}>
        <label style={{ marginRight: '0' }}>
          <strong>Codebook Type:</strong>
        </label>
        {(['STATE', 'PURPOSE', 'ERROR'] as CodebookType[]).map(type => (
          <label key={type} style={{ marginRight: '0', display: 'inline-flex', alignItems: 'center' }}>
            <input
              type="radio"
              name="codebookType"
              value={type}
              checked={selectedCodebookType === type}
              onChange={() => setSelectedCodebookType(type)}
              style={{ marginRight: '6px' }}
            />
            <span style={{ marginLeft: '5px' }}>{type}</span>
          </label>
        ))}
      </div>
      <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '12px 16px', alignItems: 'center' }}>
        <label>
          <input
            type="checkbox"
            checked={showDisabled}
            onChange={() => setShowDisabled(!showDisabled)}
          />
          <span style={{ marginLeft: '8px' }}>Show disabled entries</span>
        </label>
        <label>
          <input
            type="checkbox"
            checked={isBulkEditMode}
            onChange={(e) => setIsBulkEditMode(e.target.checked)}
          />
          <span style={{ marginLeft: '8px' }}>Bulk Edit Mode</span>
        </label>
        {isBulkEditMode && (
          <>
            <button
              type="button"
              onClick={handleSaveAllEdits}
              disabled={isSavingBulk || Object.keys(editingEntries).length === 0}
              style={{
                padding: '8px 14px',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isSavingBulk ? 'not-allowed' : 'pointer',
              }}
            >
              {isSavingBulk ? 'Saving...' : `Save All (${Object.keys(editingEntries).length})`}
            </button>
            <button
              type="button"
              onClick={handleDiscardAllEdits}
              disabled={Object.keys(editingEntries).length === 0}
              style={{
                padding: '8px 14px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Discard All
            </button>
          </>
        )}
        {lastCodebookSaveMs !== null && (
          <span style={{ fontSize: '0.9rem', color: '#2d3748', background: '#edf2f7', padding: '6px 10px', borderRadius: 999 }}>
            Last {lastCodebookSaveKind === 'bulk' ? 'bulk' : 'single'} save: {lastCodebookSaveMs} ms
          </span>
        )}
      </div>

      <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f7fafc', borderRadius: '8px' }}>
        <div style={{ marginBottom: '10px' }}>
          <strong style={{ marginRight: '12px' }}>Visible columns ({selectedCodebookType}):</strong>
          <button type="button" onClick={() => setAllColumnsVisible(true)} style={{ marginRight: '8px' }}>All</button>
          <button type="button" onClick={() => setAllColumnsVisible(false)}>None</button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 16px' }}>
          {CODEBOOK_COLUMNS.map(col => (
            <label key={col.key}>
              <input
                type="checkbox"
                checked={visibleColumnsByType[selectedCodebookType][col.key]}
                onChange={() => toggleColumnVisibility(col.key)}
              />
              <span style={{ marginLeft: '6px' }}>{col.label}</span>
            </label>
          ))}
        </div>
      </div>
      {loading && <p>Loading entries...</p>}
      {entries.length === 0 && !loading && <p>No entries found</p>}
      {entries.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '860px' : 'auto' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              {CODEBOOK_COLUMNS.filter(col => visibleColumnsByType[selectedCodebookType][col.key]).map(col => (
                <th key={col.key} style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'left' }}>
                  {col.label}
                </th>
              ))}
              <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'left' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(entry => {
              const draft = editingEntries[entry.id];
              const rowIsEditing = isBulkEditMode || Boolean(draft);
              return (
                <tr key={entry.id} style={{ borderBottom: '1px solid #ddd' }}>
                  {CODEBOOK_COLUMNS.filter(col => visibleColumnsByType[selectedCodebookType][col.key]).map(col => (
                    <td key={col.key} style={{ border: '1px solid #ddd', padding: '10px' }}>
                      {renderCell(entry, draft, col.key, rowIsEditing)}
                    </td>
                  ))}
                  <td style={{ border: '1px solid #ddd', padding: '10px' }}>
                    {isBulkEditMode ? (
                      <div style={{ display: 'flex', gap: '5px', flexDirection: isMobile ? 'column' : 'row' }}>
                        {draft && (
                          <button
                            onClick={() => handleCancelEdit(entry.id)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#f44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              width: isMobile ? '100%' : 'auto',
                            }}
                          >
                            Reset Row
                          </button>
                        )}
                        {!draft && <span style={{ color: '#718096', fontSize: '0.85rem' }}>Auto-tracked</span>}
                      </div>
                    ) : draft ? (
                      <div style={{ display: 'flex', gap: '5px', flexDirection: isMobile ? 'column' : 'row' }}>
                        <button
                          onClick={() => handleSaveEntry(entry.id)}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            width: isMobile ? '100%' : 'auto',
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => handleCancelEdit(entry.id)}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            width: isMobile ? '100%' : 'auto',
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleStartEdit(entry.id)}
                        style={{
                          padding: '5px 10px',
                          backgroundColor: '#2196f3',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          width: isMobile ? '100%' : 'auto',
                        }}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
  const renderSyncTab = () => (
    <div style={{ padding: pagePadding }}>
      <h3>Shared Codebook Sync Status</h3>
      <p>
        The shared Ubyport codebook cache is automatically refreshed daily at 03:00 AM (configurable).
        This does not replace apartment-specific Ubyport credentials used for apartment submissions.
      </p>
      {loading && <p>Loading sync status...</p>}
      {syncStatus.length > 0 && (
        <div>
          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={handleForceSyncAll}
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: '#ff9800',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                width: isMobile ? '100%' : 'auto',
              }}
            >
              Force Sync All Codebooks
            </button>
          </div>
          {syncStatus.map(status => (
            <div
              key={status.type}
              style={{
                marginBottom: '15px',
                padding: '15px',
                backgroundColor: status.lastSyncOk ? '#e8f5e9' : '#ffebee',
                borderRadius: '4px',
              }}
            >
              <div style={{ marginBottom: '10px' }}>
                <h4 style={{ margin: '0 0 10px 0' }}>
                  {status.type}
                  <span style={{ marginLeft: '10px', fontSize: '0.9em' }}>
                    {status.lastSyncOk ? '✓ OK' : '✗ FAILED'}
                  </span>
                </h4>
              </div>
              <p>
                <strong>Last Synced:</strong> {formatDate(status.lastSyncedAt)}
              </p>
              {status.lastSyncError && (
                <p style={{ color: '#d32f2f' }}>
                  <strong>Error:</strong> {status.lastSyncError}
                </p>
              )}
              <button
                onClick={() => handleForceSyncType(status.type)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#2196f3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  width: isMobile ? '100%' : 'auto',
                }}
              >
                Force Sync
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
  const persistColumns = (type: CodebookType, columns: Record<ColumnKey, boolean>) => {
    localStorage.setItem(getColumnsStorageKey(type), JSON.stringify(columns));
  };

  const toggleColumnVisibility = (column: ColumnKey) => {
    setVisibleColumnsByType(prev => {
      const nextForType = { ...prev[selectedCodebookType], [column]: !prev[selectedCodebookType][column] };
      persistColumns(selectedCodebookType, nextForType);
      return {
        ...prev,
        [selectedCodebookType]: nextForType,
      };
    });
  };

  const setAllColumnsVisible = (visible: boolean) => {
    setVisibleColumnsByType(prev => {
      const nextForType = CODEBOOK_COLUMNS.reduce((acc, col) => {
        acc[col.key] = visible;
        return acc;
      }, {} as Record<ColumnKey, boolean>);
      persistColumns(selectedCodebookType, nextForType);
      return {
        ...prev,
        [selectedCodebookType]: nextForType,
      };
    });
  };

  return (
    <div style={{ padding: pagePadding }}>
      <h2>Shared Ubyport Administration</h2>
      {error && (
        <div
          style={{
            padding: '15px',
            marginBottom: '20px',
            backgroundColor: '#ffebee',
            color: '#c62828',
            borderRadius: '4px',
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: '15px',
            marginBottom: '20px',
            backgroundColor: '#e8f5e9',
            color: '#2e7d32',
            borderRadius: '4px',
          }}
        >
          {success}
        </div>
      )}
      <div style={{ marginBottom: '20px', borderBottom: '2px solid #ddd', display: 'flex', flexWrap: 'wrap', gap: isMobile ? '8px' : '0' }}>
        <button
          onClick={() => setActiveTab('config')}
          style={{
            padding: '10px 20px',
            marginRight: isMobile ? '0' : '10px',
            backgroundColor: activeTab === 'config' ? '#2196f3' : '#f0f0f0',
            color: activeTab === 'config' ? 'white' : '#000',
            border: 'none',
            borderRadius: '4px 4px 0 0',
            cursor: 'pointer',
            width: isMobile ? '100%' : 'auto',
          }}
        >
          Configuration
        </button>
        <button
          onClick={() => setActiveTab('codebook')}
          style={{
            padding: '10px 20px',
            marginRight: isMobile ? '0' : '10px',
            backgroundColor: activeTab === 'codebook' ? '#2196f3' : '#f0f0f0',
            color: activeTab === 'codebook' ? 'white' : '#000',
            border: 'none',
            borderRadius: '4px 4px 0 0',
            cursor: 'pointer',
            width: isMobile ? '100%' : 'auto',
          }}
        >
          Codebook Management
        </button>
        <button
          onClick={() => setActiveTab('sync')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'sync' ? '#2196f3' : '#f0f0f0',
            color: activeTab === 'sync' ? 'white' : '#000',
            border: 'none',
            borderRadius: '4px 4px 0 0',
            cursor: 'pointer',
            width: isMobile ? '100%' : 'auto',
          }}
        >
          Sync Status
        </button>
      </div>
      {activeTab === 'config' && renderConfigTab()}
      {activeTab === 'codebook' && renderCodebookTab()}
      {activeTab === 'sync' && renderSyncTab()}
    </div>
  );
};

export default UbyportAdmin;
